import query from '@design/query';
import theme, { ThemeColorName } from '@design/theme';
import useIsMobile from '@util/customHooks/useIsMobile';
import { SimpleComponent } from '@util/types';
import { usePathname } from 'next/navigation';
import styled from 'styled-components';

export interface BlockStylesProps {
  $squish?: boolean;
  $full?: boolean; //full bleed
  $fullOnMobile?: boolean; //full bleed on mobile only
  $background?: ThemeColorName; //inputs color for block that extends to the entire viewport
  $backgroundInner?: ThemeColorName; //inputs color for area within the block that extends to the entire viewport
  $className?: string; // to allow styled components to be passed in
}

// generate css calculate rule that uses padding to lock a virtual max width
const extraPaddingCalc = (marginPx: number) =>
  `calc(${marginPx}px + max(0px, 50% - ${theme.breakpointMap.max} / 2))`;

const BlockStyles = styled.section<BlockStylesProps>(
  ({ $squish, $background, $full, $fullOnMobile, $className }: BlockStylesProps) => {
    // TODO: use styled components CSSObject Type
    const css: Record<string, any> = {
      boxSizing: 'border-box', // allows padding to size inner content
      width: '100%',
      // prevent margin collapse to preserve apparent padding when bg set
      '&>*': { overflow: 'hidden' },
    };

    const pathname = usePathname();
    const localePaths = ['en', 'fr-CA', 'fr-ca', 'es', 'en-US'];
    const useMorePadding =
      localePaths.some((locale) => pathname === `/${locale}`) || pathname === '/';

    // add background if specified
    if ($background) {
      css.backgroundColor = theme.colors[$background];
    }

    const padding = {
      mobile: {
        x: `${$full || $fullOnMobile ? 0 : theme.spacingPx.marginMobile}px`,
        y: `${$full ? 0 : theme.spacingPx.x6}px`,
      },
      tablet: {
        x: `${$full ? 0 : theme.spacingPx.marginTablet}px`,
        y: `${$full ? 0 : $squish ? theme.spacingPx.lg : theme.spacingPx.x6}px`,
      },
      desktop: {
        x: `0`,
        y: `${
          $full
            ? 0
            : $squish
            ? theme.spacingPx.lg
            : useMorePadding
            ? theme.spacingPx.x10
            : theme.spacingPx.x8
        }px`,
      },
    };

    css.padding = `${padding.mobile.y} ${padding.mobile.x}`;

    const desktopPadding = !useMorePadding
      ? `${padding.desktop.y} ${padding.desktop.x} 80px ${padding.desktop.x}`
      : `${padding.desktop.y} ${padding.desktop.x}`;

    css[`@media (${query.atLeast.tablet})`] = {
      padding: `${padding.tablet.y} ${padding.tablet.x}`,
    };
    css[`@media (${query.atLeast.desktop})`] = {
      padding: desktopPadding,
    };
    if (
      $className?.includes('wide-stacked-text-image-block-cn') ||
      $className?.includes('contact-form-cn') ||
      $className?.includes('wide-text-image-cn') ||
      $className?.includes('stacked-text-image-form-cn')
    ) {
      const hasPadding =
        $className?.includes('contact-form-cn') ||
        $className?.includes('wide-text-image-cn') ||
        $className?.includes('wide-stacked-text-image-block-cn');
      const isMobileScreenSize = useIsMobile();
      css['@media (min-width: 375px)'] = {
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        padding: !isMobileScreenSize ? null : 'unset',
        boxSizing: 'border-box',
      };
      css[`@media (${query.atLeast.tablet})`] = {
        padding: 'unset',
      };
      css[`@media (${query.atLeast.max}), (${query.atLeast.desktop})`] = {
        width: 'unset',
        marginLeft: 'unset',
        padding: hasPadding ? '64px 0' : 'unset',
        boxSizing: 'unset',
      };
    }

    if ($background === 'secondaryBg' && $className?.includes('home-page-about-us-content')) {
      css['@media (min-width: 375px)'] = {
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        padding: `${theme.spacingPx.x6}px 20px`,
        boxSizing: 'border-box',
      };
      css[`@media (${query.atLeast.desktop})`] = {
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        padding: `${theme.spacingPx.x6}px 20px`,
        boxSizing: 'border-box',
      };
      css[`@media (${query.atLeast.max})`] = {
        width: '1440px',
        marginLeft: 'calc(-720px + 50%)',
        padding: `${theme.spacingPx.x6}px 193px 128px 193px`,
      };
    }

    if ($className?.includes('advisor-bio-contact-form')) {
      css[`@media (${query.atLeast.desktop})`] = {
        marginTop: '80px',
      };

      css[`@media (${query.below.desktop})`] = {
        marginTop: '64px',
      };
    }

    return css;
  },
);

export default BlockStyles as SimpleComponent<BlockStylesProps>;
